import React from 'react';

// Fonction utilitaire pour supprimer les doublons (garder la réponse la plus récente)
const removeDuplicateResponses = (responses: any[]) => {
  const studentMap = new Map();
  
  responses.forEach(response => {
    const studentId = response.studentId;
    if (!studentMap.has(studentId)) {
      studentMap.set(studentId, []);
    }
    studentMap.get(studentId).push(response);
  });
  
  const cleanedResponses: any[] = [];
  studentMap.forEach((studentResponses, studentId) => {
    studentResponses.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    cleanedResponses.push(studentResponses[0]);
  });
  
  return cleanedResponses;
};

interface StudentRankingProps {
  questions: any[];
  session: any;
}

interface StudentStats {
  studentId: string;
  studentName: string;
  totalResponses: number;
  correctResponses: number;
  accuracyRate: number;
  participationRate: number;
  rank: number;
  // Nouveaux champs pour les questions ouvertes
  openEndedScore: number;
  totalOpenEndedResponses: number;
}

export const StudentRanking: React.FC<StudentRankingProps> = ({ questions, session }) => {
  // Filtrer les questions ouvertes évaluées (accessible dans tout le composant)
  const evaluatedOpenEndedQuestions = questions.filter(question => 
    question.type === 'open_ended' && 
    question.responses && 
    question.responses.some((r: any) => r.concordanceScore !== null && r.concordanceScore !== undefined)
  );

  // Calculer les statistiques pour chaque étudiant
  const calculateStudentStats = (): StudentStats[] => {
    const studentMap = new Map<string, StudentStats>();
    
    // Initialiser les statistiques pour chaque étudiant
    if (session.participants) {
      session.participants.forEach((participant: any) => {
        studentMap.set(participant.studentId, {
          studentId: participant.studentId,
          studentName: participant.studentName || `Étudiant ${participant.studentId}`,
          totalResponses: 0,
          correctResponses: 0,
          accuracyRate: 0,
          participationRate: 0,
          rank: 0,
          openEndedScore: 0,
          totalOpenEndedResponses: 0
        });
      });
    }
    
    
    // Filtrer les questions qui ont une bonne réponse définie (QCM et Vrai/Faux)
    const questionsWithCorrectAnswer = questions.filter(question => 
      question.correctAnswer !== undefined && 
      question.correctAnswer !== null && 
      question.correctAnswer !== '' &&
      (question.type === 'multiple_choice' || question.type === 'true_false')
    );
    
    
    // Analyser les réponses de toutes les questions (pour la participation) - sans doublons
    questions.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats) {
            studentStats.totalResponses++;
          }
        });
      }
    });
    
    // Analyser les réponses correctes (QCM/VF + Questions ouvertes évaluées) - sans doublons
    questionsWithCorrectAnswer.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats) {
            // Vérifier si la réponse est correcte pour QCM/VF
            if (question.type === 'multiple_choice' || question.type === 'true_false') {
              const responseIndex = parseInt(response.answer);
              const correctIndex = parseInt(question.correctAnswer);
              if (responseIndex === correctIndex) {
                studentStats.correctResponses++;
              }
            }
          }
        });
      }
    });
    
    // Analyser les questions ouvertes évaluées - sans doublons
    evaluatedOpenEndedQuestions.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats && response.concordanceScore !== null && response.concordanceScore !== undefined) {
            // Ajouter le score de concordance au score total des questions ouvertes
            studentStats.openEndedScore += response.concordanceScore;
            studentStats.totalOpenEndedResponses++;
            
            // Considérer qu'un score >= 50% est une "réponse correcte" pour le classement
            if (response.concordanceScore >= 50) {
              studentStats.correctResponses++;
            }
          }
        });
      }
    });
    
    // Calculer les taux et classer
    const studentStatsArray = Array.from(studentMap.values()).map(stats => {
      // Taux de participation (réponses / nombre total de questions)
      stats.participationRate = questions.length > 0 ? (stats.totalResponses / questions.length) * 100 : 0;
      
      // Taux de réussite (réponses correctes / réponses aux questions évaluables)
      const responsesToEvaluableQuestions = questionsWithCorrectAnswer.reduce((total, question) => {
        const cleanedResponses = removeDuplicateResponses(question.responses || []);
        const studentResponses = cleanedResponses.filter((r: any) => r.studentId === stats.studentId);
        return total + studentResponses.length;
      }, 0);
      
      // Ajouter les réponses aux questions ouvertes évaluées - sans doublons
      const responsesToOpenEndedQuestions = evaluatedOpenEndedQuestions.reduce((total, question) => {
        const cleanedResponses = removeDuplicateResponses(question.responses || []);
        const studentResponses = cleanedResponses.filter((r: any) => r.studentId === stats.studentId && r.concordanceScore !== null && r.concordanceScore !== undefined);
        return total + studentResponses.length;
      }, 0);
      
      const totalEvaluableResponses = responsesToEvaluableQuestions + responsesToOpenEndedQuestions;
      stats.accuracyRate = totalEvaluableResponses > 0 ? (stats.correctResponses / totalEvaluableResponses) * 100 : 0;
      
      return stats;
    });
    
    // Classer par taux de réussite, puis par taux de participation, puis par nombre de réponses
    studentStatsArray.sort((a, b) => {
      // D'abord par taux de réussite (décroissant)
      if (Math.abs(a.accuracyRate - b.accuracyRate) > 0.1) {
        return b.accuracyRate - a.accuracyRate;
      }
      // Ensuite par taux de participation (décroissant)
      if (Math.abs(a.participationRate - b.participationRate) > 0.1) {
        return b.participationRate - a.participationRate;
      }
      // Enfin par nombre total de réponses (décroissant)
      return b.totalResponses - a.totalResponses;
    });
    
    // Assigner les rangs
    studentStatsArray.forEach((stats, index) => {
      stats.rank = index + 1;
    });
    
    return studentStatsArray;
  };
  
  const studentStats = calculateStudentStats();
  
  // Obtenir la couleur du rang
  const getRankColor = (rank: number): string => {
    if (rank === 1) return '#ffd700'; // Or
    if (rank === 2) return '#c0c0c0'; // Argent
    if (rank === 3) return '#cd7f32'; // Bronze
    return '#6c757d'; // Gris
  };
  
  // Obtenir l'icône du rang
  const getRankIcon = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };
  
  if (studentStats.length === 0) {
    return (
      <div className="student-ranking">
        <h3>Classement des Étudiants</h3>
        <div className="no-data">
          <p>Aucun étudiant n'a participé à cette session.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="student-ranking">
      <div className="ranking-summary">
        <div className="summary-stats">
          <div className="summary-item">
            <span className="summary-label">Total participants:</span>
            <span className="summary-value">{studentStats.length}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Questions posées:</span>
            <span className="summary-value">{questions.length}</span>
          </div>
          {evaluatedOpenEndedQuestions.length > 0 && (
            <div className="summary-item">
              <span className="summary-label">Questions ouvertes évaluées:</span>
              <span className="summary-value">{evaluatedOpenEndedQuestions.length}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="ranking-list">
        {studentStats.map((student) => (
          <div key={student.studentId} className="ranking-item">
            <div className="rank-badge" style={{ backgroundColor: getRankColor(student.rank) }}>
              {getRankIcon(student.rank)}
            </div>
            
            <div className="student-info">
              <div className="student-name">{student.studentName}</div>
              <div className="student-id">ID: {student.studentId}</div>
            </div>
            
            <div className="student-stats">
              <div className="stat-row">
                <span className="stat-label">Réponses:</span>
                <span className="stat-value">{student.totalResponses}/{questions.length}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Correctes:</span>
                <span className="stat-value correct">{student.correctResponses}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Participation:</span>
                <span className="stat-value">{student.participationRate.toFixed(2)}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Réussite:</span>
                <span className="stat-value success">{student.accuracyRate.toFixed(2)}%</span>
              </div>
              {/* {student.totalOpenEndedResponses > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Questions ouvertes:</span>
                  <span className="stat-value open-ended">
                    {(student.openEndedScore / student.totalOpenEndedResponses).toFixed(1)}% ({student.totalOpenEndedResponses} réponse{student.totalOpenEndedResponses > 1 ? 's' : ''})
                  </span>
                </div>
              )} */}
            </div>
            
            <div className="performance-bar">
              <div 
                className="performance-fill"
                style={{ 
                  width: `${student.accuracyRate}%`,
                  backgroundColor: student.accuracyRate >= 80 ? '#28a745' : 
                                 student.accuracyRate >= 60 ? '#ffc107' : '#dc3545'
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="ranking-legend">
        <h4>Légende du classement :</h4>
        <ul>
          <li><strong>Rang :</strong> Classement basé sur le taux de réussite, puis la participation</li>
          <li><strong>Réponses :</strong> Nombre de réponses données / nombre total de questions</li>
          <li><strong>Correctes :</strong> Nombre de réponses correctes</li>
          <li><strong>Participation :</strong> Pourcentage de questions auxquelles l'étudiant a répondu</li>
          <li><strong>Réussite :</strong> Pourcentage de réponses correctes</li>
        </ul>
      </div>
    </div>
  );
};
